import React, { useMemo } from 'react';
import { toLocalYMD } from '../../../../utils/dateFmt';

const ChartBar = ({ day, index, getBarHeight }) => {
  const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
  return (
    <div key={index} className="flex-1 min-w-[20px] flex flex-col items-center">
      {/* Stacked Bar */}
      <div className="w-full flex flex-col-reverse relative">
        {/* Work Hours */}
        {day.workHours > 0 && (
          <div
            className={`w-full ${!day.isCompliant ? 'bg-red-500' : 'bg-red-300'} rounded-t`}
            style={{ height: `${getBarHeight(day.workHours) * 1.92}px` }}
            title={`Work: ${day.workHours.toFixed(1)}h`}
          />
        )}
        {/* Rest Hours */}
        <div
          className="w-full bg-green-400 rounded-t"
          style={{ height: `${getBarHeight(day.restHours) * 1.92}px` }}
          title={`Rest: ${day.restHours.toFixed(1)}h`}
        />
      </div>
      
      {/* Date Label */}
      <div className={`text-[10px] mt-1 ${isWeekend ? 'font-semibold' : ''} ${!day.isCompliant ? 'text-red-600' : 'text-gray-600'}`}>
        {day.date.getDate()}
      </div>
    </div>
  );
};

const HoursChart = ({ selectedMonth, selectedCrew, hoursData = {} }) => {
  // Get days in month
  const daysInMonth = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 1; i <= lastDay; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  }, [selectedMonth]);

  // Get work hours for each day - properly handle both field names and periods
  const dailyHours = useMemo(() => {
    if (!selectedCrew) return [];
    
    return daysInMonth.map(date => {
      const dateStr = toLocalYMD(date);
      const dayData = hoursData[selectedCrew]?.[dateStr];
      
      if (!dayData) {
        // No data = full rest day
        return {
          date,
          workHours: 0,
          restHours: 24,
          isCompliant: true
        };
      }
      
      // Check all possible field names for work/rest hours
      let workHours = dayData.workHours ?? dayData.totalWork ?? 0;
      let restHours = dayData.restHours ?? dayData.totalRest ?? 0;
      
      // If we have periods array (Phase 3), the totals should already be calculated
      // The grid in HoursRecordsView handles the detailed period-based display
      
      // Ensure we have valid numbers
      if (workHours === 0 && restHours === 0) {
        // If both are 0, treat as full rest day
        restHours = 24;
      } else if (workHours + restHours !== 24) {
        // Normalize to 24 hours if there's a mismatch
        if (workHours > 0 && restHours === 0) {
          restHours = 24 - workHours;
        } else if (restHours > 0 && workHours === 0) {
          workHours = 24 - restHours;
        }
      }
      
      return {
        date,
        workHours,
        restHours,
        isCompliant: dayData.isCompliant !== false && restHours >= 10
      };
    });
  }, [selectedCrew, daysInMonth, hoursData]);

  // Calculate bar height percentage
  const getBarHeight = (hours) => (hours / 24) * 100;

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Hours of Rest Chart</h3>
      
      {/* Chart Container */}
      <div className="relative">
        {/* Y-Axis Labels (Hours) */}
        <div className="absolute left-0 top-0 bottom-8 w-8 flex flex-col justify-between text-xs text-gray-500">
          <span>24h</span>
          <span>18h</span>
          <span>12h</span>
          <span>6h</span>
          <span>0h</span>
        </div>
        
        {/* Chart Area */}
        <div className="ml-10 overflow-x-auto">
          <div className="relative h-48 min-w-max">
            {/* Grid Lines */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="border-t border-gray-200" />
              ))}
            </div>
            
            {/* Bars */}
            <div className="relative h-full flex items-end gap-1 px-2">
              {dailyHours.map((day, index) => (
                <ChartBar 
                  key={index} 
                  day={day} 
                  index={index} 
                  getBarHeight={getBarHeight} 
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* X-Axis Label */}
        <div className="text-center text-xs text-gray-500 mt-2">
          Days of {selectedMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </div>
      </div>
      
      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-400 rounded" />
          <span>Rest Hours</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-300 rounded" />
          <span>Work Hours</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span>Non-compliant</span>
        </div>
      </div>
    </div>
  );
};

export default HoursChart;